/**
 * CampaignGroupsPanel
 *
 * Slide-in groups picker for the create/edit campaign panel — the same panel
 * pattern Assets use (New Asset → EditGroup). The campaign form's GroupSelector
 * '+' opens this as a sibling column; the form chips stay visible and update
 * live as groups are toggled here.
 *
 * State bridge: MultiItemSelector stashes the form's setter + current selection
 * in `multiItemSelectorStore` when the '+' is pressed. This panel seeds from
 * that snapshot (popInitialItems) and pushes every change back via `notify`
 * (which calls the form's setter without clearing it, so the panel stays live).
 * The store callback is cleared on unmount.
 */
import {
  useAllWorkspaceGroups,
  useCreateGroup,
} from '@gripp/shared-logic';
import {
  LoadingIndicator,
  MultiItemSelectorModal,
  useSlideLayout,
} from '@gripp/shared-ui';
import { multiItemSelectorStore } from 'libs/shared-ui/src/components/forms/multiItemSelectorStore';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

type GroupItem = { id: string; name: string };

export const CampaignGroupsPanel = ({
  workspaceId,
}: {
  workspaceId: string;
}) => {
  const { t } = useTranslation();
  const { setColumnTitle } = useSlideLayout();
  const { groups: availableGroups = [], isLoading } =
    useAllWorkspaceGroups(workspaceId);
  const { createGroup } = useCreateGroup();

  // Seed once from the opener's current selection. The component is keyed by an
  // open counter in campaignsPage, so each '+' press remounts and re-seeds.
  const [selected, setSelected] = useState<GroupItem[]>(() =>
    multiItemSelectorStore.popInitialItems(),
  );
  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Drop the stashed callback when the panel closes so it can't fire later.
  useEffect(() => () => multiItemSelectorStore.clear(), []);

  const apply = (next: GroupItem[]) => {
    setSelected(next);
    multiItemSelectorStore.notify(next);
  };

  const allGroups = useMemo(() => {
    const map = new Map<string, GroupItem>();
    [...availableGroups, ...selected].forEach((g) => {
      if (g?.id) map.set(g.id, { id: g.id, name: g.name });
    });
    return Array.from(map.values());
  }, [availableGroups, selected]);

  const selectedIds = useMemo(
    () => new Set(selected.map((g) => g.id)),
    [selected],
  );

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    const base = query
      ? allGroups.filter((g) => g.name.toLowerCase().includes(query))
      : allGroups;
    return base.map((g) => ({ ...g, isActive: selectedIds.has(g.id) }));
  }, [allGroups, search, selectedIds]);

  const canCreateItem =
    !!search.trim() &&
    !allGroups.some(
      (g) => g.name.toLowerCase() === search.trim().toLowerCase(),
    );

  const handleItemToggle = (item: GroupItem) => {
    if (selectedIds.has(item.id)) {
      apply(selected.filter((g) => g.id !== item.id));
      return;
    }
    apply([...selected, { id: item.id, name: item.name }]);
  };

  const handleToggleAll = (selectAll: boolean) => {
    if (selectAll) {
      const toAdd = filteredItems
        .filter((i) => !selectedIds.has(i.id))
        .map((i) => ({ id: i.id, name: i.name }));
      apply([...selected, ...toAdd]);
      return;
    }
    const ids = new Set(filteredItems.map((i) => i.id));
    apply(selected.filter((g) => !ids.has(g.id)));
  };

  const handleCreateGroup = async (name: string): Promise<GroupItem> => {
    const created = await createGroup(name);
    return { id: created.id, name: created.name };
  };

  const handleCreatePress = async () => {
    if (!search.trim()) return;
    setIsCreating(true);
    try {
      const created = await handleCreateGroup(search.trim());
      apply([...selected, created]);
      setSearch('');
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    setColumnTitle(`${t('campaigns.create.groups', 'Groups')} (${selected.length})`);
  }, [setColumnTitle, t, selected.length]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <View style={styles.container}>
      <MultiItemSelectorModal
        showOptions
        search={search}
        setSearch={setSearch}
        searchPlaceholder={t('common.searchOrCreate', 'Search or create')}
        filteredItems={filteredItems}
        onItemToggle={handleItemToggle}
        onToggleAll={handleToggleAll}
        canCreateItem={canCreateItem}
        onCreateItem={handleCreateGroup}
        onCreatePress={handleCreatePress}
        isCreating={isCreating}
        createButtonText={t('common.create', 'Create')}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
